"use client";
import { isLostStage, isWonDeal, LOST_STAGES, DEAL_WON_STAGE } from "@/lib/constants/deal-stages";
import type { DealStage } from "@/lib/types/database/deals";

import React from "react";
import type { Deal } from "@/lib/types/database/deals";
import { Card, CardContent } from "@wtc/ui/components/card";
import { Badge } from "@wtc/ui/components/badge";

interface DealDetailMetricsPanelProps {
  deal: Deal;
}

const STAGE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  lead: { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-800 dark:text-gray-200", label: "Lead" },
  qualified: { bg: "bg-blue-100 dark:bg-blue-900/40", text: "text-blue-800 dark:text-blue-200", label: "Qualified" },
  discovery: { bg: "bg-cyan-100 dark:bg-cyan-900/40", text: "text-cyan-800 dark:text-cyan-200", label: "Discovery" },
  proposal: { bg: "bg-purple-100 dark:bg-purple-900/40", text: "text-purple-800 dark:text-purple-200", label: "Proposal" },
  negotiation: { bg: "bg-orange-100 dark:bg-orange-900/40", text: "text-orange-800 dark:text-orange-200", label: "Negotiation" },
  contract: { bg: "bg-yellow-100 dark:bg-yellow-900/40", text: "text-yellow-800 dark:text-yellow-200", label: "Contract" },
  won: { bg: "bg-green-100 dark:bg-green-900/40", text: "text-green-800 dark:text-green-200", label: "Won" },
  lost: { bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-800 dark:text-red-200", label: "Lost" },
};

const getMarginColor = (margin: number | undefined): string => {
  if (margin === undefined) return "text-gray-600 dark:text-gray-400";
  if (margin >= 30) return "text-green-600 dark:text-green-400";
  if (margin >= 15) return "text-yellow-600 dark:text-yellow-400";
  return "text-red-600 dark:text-red-400";
};

const getMarginBgColor = (margin: number | undefined): string => {
  if (margin === undefined) return "bg-gray-50 dark:bg-gray-900";
  if (margin >= 30) return "bg-green-50 dark:bg-green-950/40";
  if (margin >= 15) return "bg-yellow-50 dark:bg-yellow-950/40";
  return "bg-red-50 dark:bg-red-950/40";
};

const getDaysUntilClose = (closeDate: string | undefined): number | null => {
  if (!closeDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const close = new Date(closeDate);
  close.setHours(0, 0, 0, 0);
  const diffTime = close.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export function DealDetailMetricsPanel({ deal }: DealDetailMetricsPanelProps) {
  const stageConfig = STAGE_COLORS[deal.stage] || STAGE_COLORS.lead;
  const daysUntilClose = getDaysUntilClose(deal.close_date);
  const marginColor = getMarginColor(deal.margin_percentage);
  const marginBgColor = getMarginBgColor(deal.margin_percentage);

  // Profit ($) = revenue − cost. Mirrors the DB margin trigger
  // (margin_percentage = (revenue − cost) / revenue × 100); shown so the dollar
  // figure is done for the user, not just the percentage. Only when both exist.
  const profit =
    deal.revenue != null && deal.cost != null ? Number(deal.revenue) - Number(deal.cost) : undefined;

  return (
    <div className="space-y-4 sticky top-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
      {/* Stage Badge */}
      <div>
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Deal Stage
        </p>
        <Badge className={`${stageConfig.bg} ${stageConfig.text} text-xs font-semibold px-2 py-1`}>
          {stageConfig.label}
        </Badge>
      </div>

      {/* Revenue Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/40 dark:to-blue-900/40">
        <CardContent className="pt-4">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
            Revenue
          </p>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: deal.currency,
              maximumFractionDigits: 0,
            }).format(deal.revenue)}
          </p>
          {deal.is_recurring && deal.monthly_amount && (
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
              MRR:{" "}
              {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: deal.currency,
                maximumFractionDigits: 0,
              }).format(deal.monthly_amount)}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Close Probability Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-purple-950/40 dark:to-purple-900/40">
        <CardContent className="pt-4">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
            Close Probability
          </p>
          <div className="flex items-end gap-3">
            <p className="text-3xl font-bold text-purple-900 dark:text-purple-200">{deal.close_probability}%</p>
            <div className="flex-1 h-2 bg-purple-200 dark:bg-purple-900/50 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-600 rounded-full transition-all"
                style={{ width: `${deal.close_probability}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Close Date Card */}
      {deal.close_date && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-950/40 dark:to-orange-900/40">
          <CardContent className="pt-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
              Close Date
            </p>
            <p className="text-lg font-semibold text-orange-900 dark:text-orange-200">
              {new Date(deal.close_date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
            {daysUntilClose !== null && (
              <p className={`text-xs mt-2 font-medium ${daysUntilClose < 0 ? "text-red-600 dark:text-red-400" : "text-orange-700 dark:text-orange-300"}`}>
                {daysUntilClose < 0
                  ? `${Math.abs(daysUntilClose)} days overdue`
                  : daysUntilClose === 0
                    ? "Due today"
                    : `${daysUntilClose} days remaining`}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Financial Metrics */}
      {(deal.cost !== undefined || deal.margin_percentage !== undefined) && (
        <Card className={`border-0 shadow-sm ${marginBgColor}`}>
          <CardContent className="pt-4">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
              Financial Health
            </p>
            <div className="space-y-2">
              {deal.cost !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Cost</span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: deal.currency,
                      maximumFractionDigits: 0,
                    }).format(deal.cost)}
                  </span>
                </div>
              )}
              {profit !== undefined && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Profit</span>
                  <span
                    className={`font-semibold ${
                      profit >= 0
                        ? "text-green-600 dark:text-green-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: deal.currency,
                      maximumFractionDigits: 0,
                    }).format(profit)}
                  </span>
                </div>
              )}
              {deal.margin_percentage !== undefined && (
                <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Margin</span>
                  <span className={`font-bold text-lg ${marginColor}`}>
                    {Number(deal.margin_percentage).toFixed(1)}%
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Indicators */}
      <div className="space-y-2 pt-2">
        {deal.is_committed && (
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/40 rounded-md border border-green-200 dark:border-green-800">
            <div className="w-2 h-2 bg-green-600 rounded-full" />
            <span className="text-sm font-medium text-green-800 dark:text-green-200">Committed</span>
          </div>
        )}
        {deal.is_recurring && (
          <div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-950/40 rounded-md border border-blue-200 dark:border-blue-800">
            <div className="w-2 h-2 bg-blue-600 rounded-full" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">Recurring Revenue</span>
          </div>
        )}
        {isWonDeal(deal.stage) && deal.won_at && (
          <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/40 rounded-md border border-green-200 dark:border-green-800">
            <div className="w-2 h-2 bg-green-600 rounded-full" />
            <span className="text-sm font-medium text-green-800 dark:text-green-200">
              Won on {new Date(deal.won_at).toLocaleDateString()}
            </span>
          </div>
        )}
        {isLostStage(deal.stage) && deal.lost_at && (
          <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-950/40 rounded-md border border-red-200 dark:border-red-800">
            <div className="w-2 h-2 bg-red-600 rounded-full" />
            <span className="text-sm font-medium text-red-800 dark:text-red-200">
              Lost on {new Date(deal.lost_at).toLocaleDateString()}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
